// OP-3605 — A peça (ver ESL1): os quatro eixos, o razão dela, o que a venda
// testemunha e as ações de quem cuida do patrimônio — manutenção (ESL15) e
// proposta de baixa (ESL13, decidida por ESL14 na tela de baixas).
import { FC, useMemo, useState } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { useEstoqueHistoricoUnidade, useEstoqueRemessaDetalhe, useEstoqueUnidade, useEstoqueUsoUnidade } from '../../../../hooks/estoque'
import { usePerfilEstoque } from '../../../../hooks/estoque/perfil'
import { enviarManutencaoEstoqueUnidade, estornarEstoqueMovimento, proporEstoqueBaixa, retornarManutencaoEstoqueUnidade } from '../../../../Service/estoque'
import { HistoricoUnidadeRow } from '../../../../types/estoque'
import {
  BD, Button, Card, DetailLayout, Field, Input, ListaCartoes, PageSubtitle, PageTitle, Spinner, StatusBadge, TabelaWrap, Table,
} from '../../styles'
import { fmtDate, fmtDateTime, fmtMoney } from '../../statusUi'
import { cadeiaDaPeca, leituraDaRemessa, lerHistorico, remessaParaBuscar } from '../cadeia'
import { CartaoLinha } from '../CartaoLinha'
import { AcaoDecisao, Decisao } from '../Decisao'
import { FaixaDaVida } from '../FaixaDaVida'
import { tratarErro } from '../erros'
import { RefLink } from '../RefLink'
import { TIPO_MOVIMENTO_LABEL, ehLancamentoDeEstorno } from '../Razao/regras'
import {
  ChipDaPeca, NIVEL_UI, extravioParaReaparecer, ficouDepois, impedimentoManutencao, impedimentoProporBaixa, impedimentoReaparecimento, impedimentoRetornoManutencao, leituraDaPeca, ondeEsta,
} from './regras'
import { LOCAL_EXECUTION } from '../../../../utils/featureFlag'

const Bloco: FC<{ titulo: string; children: any }> = ({ titulo, children }) => (
  <Card style={{ padding: 16, marginBottom: 16 }}><div style={{ fontWeight: 600, marginBottom: 8 }}>{titulo}</div>{children}</Card>
)

export const EstoqueEquipamento: FC = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const idNum = Number(id) || null
  const { pode } = usePerfilEstoque()
  const podeManutencao = pode('MANUTENCAO')
  const podeBaixa = pode('PROPOR_BAIXA')
  const podeEstornar = pode('ESTORNAR')
  const { data: u, isLoading, isError, isUnauthorized, mutate } = useEstoqueUnidade(idNum)
  const historicoQ = useEstoqueHistoricoUnidade(idNum)
  const usoQ = useEstoqueUsoUnidade(idNum)
  const vida = useMemo(() => lerHistorico(historicoQ.data), [historicoQ.data])
  const remessaDaCadeiaQ = useEstoqueRemessaDetalhe(remessaParaBuscar(vida))
  const [busy, setBusy] = useState(false)
  const [fornecedor, setFornecedor] = useState('')
  const [motivoRetorno, setMotivoRetorno] = useState('')
  const [motivoBaixa, setMotivoBaixa] = useState('')
  const [motivoReaparecimento, setMotivoReaparecimento] = useState('')

  const sessaoVencida = isUnauthorized || historicoQ.isUnauthorized || usoQ.isUnauthorized || remessaDaCadeiaQ.isUnauthorized
  if (sessaoVencida && !LOCAL_EXECUTION) return <Navigate to="/auth" replace />
  if (isLoading) return <Card><div style={{ textAlign: 'center', padding: 24 }}><Spinner /></div></Card>
  if (isError || !u) {
    return (
      <>
        <PageTitle>Equipamento</PageTitle>
        <Card><div style={{ padding: 24, color: BD.grayText }}>
          Peça não encontrada ou fora do seu escopo. <Button $variant="secondary" onClick={() => navigate('/interno/estoque/equipamentos')}>Voltar</Button>
        </div></Card>
      </>
    )
  }

  /**
   * Estado, razão e uso mudam juntos depois de uma ação.
   *
   * O uso entra porque ele é calculado a partir do LOCAL atual e da chegada
   * registrada no razão: enviar para manutenção muda o local, e sem revalidar
   * o nível de certeza continuaria descrevendo o lugar anterior, ao lado de um
   * cabeçalho já atualizado. A falha dele fica isolada — o uso depende da
   * plataforma de venda e não pode derrubar a ação que acabou de dar certo.
   */
  const revalidar = async () => {
    await Promise.all([mutate(), historicoQ.mutate()])
    try { await usoQ.mutate() } catch { /* o bloco do uso mostra o próprio erro */ }
  }

  const executar = async (acao: () => Promise<any>, ok: string, limpar?: () => void) => {
    setBusy(true)
    try {
      await acao()
      toast.success(ok)
      if (limpar) limpar()
      await revalidar()
    } catch (err) {
      const e = tratarErro(err)
      if (e.tipo === 'sessao' && !LOCAL_EXECUTION) { navigate('/auth'); return }
      toast.error(e.texto)
      if (e.tipo === 'estado') await revalidar()
    } finally {
      setBusy(false)
    }
  }

  const leitura = leituraDaPeca(u)
  const historicoPronto = !historicoQ.isLoading && !historicoQ.isError
  const cadeia = cadeiaDaPeca({
    historico: vida,
    serial: u.serial,
    remessa: leituraDaRemessa({
      falhou: !!remessaDaCadeiaQ.isError,
      codigoErro: remessaDaCadeiaQ.isError ? tratarErro(remessaDaCadeiaQ.isError).codigo : null,
      remessa: remessaDaCadeiaQ.data,
    }),
  })
  const impManutencao = impedimentoManutencao(u)
  const impRetorno = impedimentoRetornoManutencao(u)
  const impBaixa = impedimentoProporBaixa(u)
  const uso = usoQ.data
  const nivel = uso ? NIVEL_UI[uso.nivel] : null

  const linhaHistorico = (h: HistoricoUnidadeRow, k: number) => (
    <tr key={`${h.id}-${k}`}>
      <td style={{ whiteSpace: 'nowrap', fontSize: 13 }}>{fmtDateTime(h.criadoEm)}</td>
      <td>
        {TIPO_MOVIMENTO_LABEL[h.tipo] ?? h.tipo}
        {ehLancamentoDeEstorno(h) && <div style={{ fontSize: 11, color: BD.grayText }}>estorno do #{h.movimentoEstornoId}</div>}
        {h.estornadoPorId != null && <div style={{ fontSize: 11, color: BD.grayText }}>estornado pelo #{h.estornadoPorId}</div>}
      </td>
      <td style={{ fontSize: 13 }}>{h.origemNome ?? '—'} → {h.destinoNome ?? '—'}</td>
      <td style={{ fontSize: 13 }}>{ficouDepois(h, historicoQ.data[k + 1]) ?? '—'}</td>
      <td style={{ fontSize: 13 }}><RefLink tipo={h.referenciaTipo} codigo={h.referenciaId} id={h.referenciaRefId ?? null} documento={h.referenciaDocumento} /><div style={{ fontSize: 11, color: BD.grayText }}>{h.usuarioNome}{h.motivo ? ` · ${h.motivo}` : ''}</div></td>
    </tr>
  )

  const emManutencao = u.condicao === 'EM_MANUTENCAO'
  const extraviada = u.cicloVida === 'EXTRAVIADO'
  const extravio = extravioParaReaparecer(historicoQ.data)
  const impReaparecimento = impedimentoReaparecimento({
    cicloVida: u.cicloVida, podeEstornar, baixaPropostaId: u.baixaPropostaId, extravio, motivo: motivoReaparecimento,
  })

  const acaoManutencao: AcaoDecisao = {
    rotulo: 'Enviar para manutenção',
    rodar: () => executar(() => enviarManutencaoEstoqueUnidade({ unidadeId: u.id, fornecedor: fornecedor.trim() }), 'Enviada para manutenção', () => setFornecedor('')),
    impedimento: impManutencao ?? (fornecedor.trim() ? null : 'Informe o fornecedor para onde a peça vai.'),
  }
  const acaoConsertada: AcaoDecisao = {
    rotulo: 'Voltou consertada',
    rodar: () => executar(() => retornarManutencaoEstoqueUnidade({ unidadeId: u.id, consertado: true, motivo: motivoRetorno.trim() || null }), 'Retorno registrado: consertada', () => setMotivoRetorno('')),
    impedimento: impRetorno,
  }
  const acaoSucata: AcaoDecisao = {
    rotulo: 'Voltou sem conserto',
    rodar: () => executar(() => retornarManutencaoEstoqueUnidade({ unidadeId: u.id, consertado: false, motivo: motivoRetorno.trim() }), 'Retorno registrado: sucata', () => setMotivoRetorno('')),
    impedimento: impRetorno ?? (motivoRetorno.trim() ? null : 'Sem conserto exige o motivo.'),
    variante: 'secondary' as const,
  }
  const acaoBaixa: AcaoDecisao = {
    rotulo: 'Propor baixa',
    rodar: () => executar(() => proporEstoqueBaixa(u.id, motivoBaixa.trim()), 'Baixa proposta', () => setMotivoBaixa('')),
    impedimento: impBaixa ?? (motivoBaixa.trim() ? null : 'Escreva o motivo da baixa.'),
    variante: 'secondary' as const,
  }

  const acaoReaparecimento: AcaoDecisao = {
    rotulo: 'Registrar reaparecimento',
    rodar: () => executar(
      () => estornarEstoqueMovimento((extravio as HistoricoUnidadeRow).id, motivoReaparecimento.trim()),
      'Reaparecimento registrado: a peça voltou ao estoque',
      () => setMotivoReaparecimento(''),
    ),
    impedimento: impReaparecimento,
  }

  // A peça não tem máquina de estados como o pedido: o que muda é o que faz
  // sentido agora. Em manutenção, o retorno é a ação; fora dela, o envio.
  const oferecerReaparecimento = extraviada && podeEstornar && !emManutencao
  const principal: AcaoDecisao | null = oferecerReaparecimento
    ? acaoReaparecimento
    : emManutencao
      ? (podeManutencao ? acaoConsertada : null)
      : (podeManutencao ? acaoManutencao : (podeBaixa ? acaoBaixa : null))

  const secundarias: AcaoDecisao[] = [
    ...(emManutencao && podeManutencao ? [acaoSucata] : []),
    ...(podeBaixa && principal !== acaoBaixa ? [acaoBaixa] : []),
  ]

  return (
    <DetailLayout $contido>
      <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <PageTitle>
            {u.serial}{' '}
            {leitura.chips.map((chip: ChipDaPeca) => (
              <StatusBadge key={chip.label} $bg={chip.bg} $color={chip.color} style={{ marginRight: 4 }}>{chip.label}</StatusBadge>
            ))}
          </PageTitle>
          <PageSubtitle>
            {u.itemNome} · patrimônio {u.patrimonio}{u.serialFabricante ? ` · série do fabricante ${u.serialFabricante}` : ''}
            {leitura.contexto ? <span style={{ display: 'block' }}>{leitura.contexto}</span> : <> · está em {ondeEsta(u)}</>}
            {u.terminalId != null && <> · terminal {u.terminalId}</>}
            {Number(u.riscoNaoRetorno) > 0 && <> · <span style={{ color: BD.red }}>marcada com risco de não retorno</span></>}
          </PageSubtitle>
          {u.cicloVida === 'EXTRAVIADO' && (
            <div style={{ fontSize: 13, color: '#92400E', background: '#FEF3C7', padding: '8px 12px', borderRadius: 6, marginTop: 8 }}>
              Peça declarada extraviada: está fora do patrimônio regular até a baixa. Se ela apareceu, registre o reaparecimento aqui na ficha (permissão "Estornar movimento"). Com proposta de baixa aberta, a gerência precisa recusá-la antes.
            </div>
          )}
        </div>
        <Button $variant="secondary" onClick={() => navigate('/interno/estoque/equipamentos')}>‹ Equipamentos</Button>
      </div>

      {historicoPronto && <FaixaDaVida cadeia={cadeia} />}

      <Card style={{ padding: 16, marginBottom: 16, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
        <div><div style={{ fontSize: 12, color: BD.grayText }}>Aquisição</div><div>{fmtDate(u.dataAquisicao)}{u.valorAquisicao != null ? ` · ${fmtMoney(u.valorAquisicao)}` : ''}</div><div style={{ fontSize: 12, color: BD.grayText }}>{u.fornecedor ?? 'fornecedor não informado'}{u.notaFiscal ? ` · NF ${u.notaFiscal}` : ''}</div></div>
        <div><div style={{ fontSize: 12, color: BD.grayText }}>Proprietário</div><div>{u.proprietario ?? 'BD'}</div></div>
        <div><div style={{ fontSize: 12, color: BD.grayText }}>Cadastrada em</div><div>{fmtDateTime(u.criadoEm)}</div></div>
        {u.observacao && <div><div style={{ fontSize: 12, color: BD.grayText }}>Observação</div><div style={{ fontSize: 13 }}>{u.observacao}</div></div>}
      </Card>

      <Bloco titulo="O que a venda diz">
        {usoQ.isLoading ? <div style={{ textAlign: 'center', padding: 12 }}><Spinner /></div>
          : usoQ.isError || !uso ? (
            <div style={{ fontSize: 13, color: BD.grayText }}>
              Não foi possível ler o uso desta peça na plataforma de venda.{' '}
              <Button $variant="secondary" onClick={() => usoQ.mutate()}>Tentar de novo</Button>
            </div>
          ) : (
            <>
              <div style={{ marginBottom: 8 }}>
                {nivel && <StatusBadge $bg={nivel.bg} $color={nivel.color}>{nivel.label}</StatusBadge>}
                <span style={{ fontSize: 13, color: BD.grayText, marginLeft: 8 }}>{nivel?.explica}</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
                <div><div style={{ fontSize: 12, color: BD.grayText }}>Última venda da peça</div><div>{fmtDateTime(uso.ultimaVendaPropria)}</div></div>
                <div>
                  <div style={{ fontSize: 12, color: BD.grayText }}>Sem vender há</div>
                  <div style={{ color: uso.parada ? BD.red : undefined }}>{uso.diasSemVender == null ? 'nunca vendeu' : `${uso.diasSemVender} dia${uso.diasSemVender === 1 ? '' : 's'}`}</div>
                  {uso.parada && <div style={{ fontSize: 12, color: BD.red }}>parada</div>}
                </div>
                <div><div style={{ fontSize: 12, color: BD.grayText }}>Última venda no ponto</div><div>{fmtDateTime(uso.ultimaVendaNoLocal)}</div></div>
                <div><div style={{ fontSize: 12, color: BD.grayText }}>Da entrega à primeira venda</div><div>{uso.diasAteVender == null ? '—' : `${uso.diasAteVender} dia${uso.diasAteVender === 1 ? '' : 's'}`}</div></div>
                <div><div style={{ fontSize: 12, color: BD.grayText }}>Pareada com terminal</div><div>{uso.pareado ? `sim${uso.terminalTipo ? ` (${uso.terminalTipo})` : ''}` : 'não'}</div></div>
              </div>
            </>
          )}
      </Bloco>

      {(podeManutencao || podeBaixa || oferecerReaparecimento) && (
        <Bloco titulo="Cuidar da peça">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
            {oferecerReaparecimento && (
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>A peça apareceu</div>
                <div style={{ fontSize: 12, color: BD.grayText, marginBottom: 6 }}>
                  Registra no razão o estorno do extravio e devolve a peça ao estoque, com a condição que ela tinha. Perdida no caminho, ela volta para a praça-base; perdida numa contagem, fica na praça onde foi contada. Se veio de uma remessa, a linha da remessa passa a constar como devolvida.
                </div>
                <Field><label htmlFor="motivo-reaparecimento">Onde e como apareceu</label><Input id="motivo-reaparecimento" value={motivoReaparecimento} maxLength={400} onChange={(e) => setMotivoReaparecimento(e.target.value)} /></Field>
                {impReaparecimento && <div style={{ fontSize: 12, color: BD.grayText, marginTop: 8 }}>{impReaparecimento}</div>}
              </div>
            )}
            {podeManutencao && u.condicao !== 'EM_MANUTENCAO' && (
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Enviar para manutenção</div>
                <div style={{ fontSize: 12, color: BD.grayText, marginBottom: 6 }}>A peça sai da praça para o fornecedor. O razão registra a saída; o retorno decide se consertou.</div>
                {u.cicloVida === 'EXTRAVIADO' && (
                  // O domínio permite o movimento, mas ele não regulariza nada:
                  // manutenção mexe em situação e condição, nunca no ciclo de
                  // vida. Dizer isso evita que a tela seja lida como o caminho
                  // de "a peça apareceu".
                  <div style={{ fontSize: 12, color: '#92400E', marginBottom: 6 }}>
                    Esta peça está como extraviada, e enviar para manutenção não muda isso: ela continua fora do patrimônio regular.
                  </div>
                )}
                <Field><label>Fornecedor</label><Input value={fornecedor} maxLength={160} disabled={!!impManutencao} onChange={(e) => setFornecedor(e.target.value)} /></Field>
                {impManutencao && <div style={{ fontSize: 12, color: BD.grayText, marginTop: 8 }}>{impManutencao}</div>}
              </div>
            )}
            {podeManutencao && u.condicao === 'EM_MANUTENCAO' && (
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Retorno da manutenção</div>
                <div style={{ fontSize: 12, color: BD.grayText, marginBottom: 6 }}>Sem conserto a peça volta como SUCATA, com motivo. Isso não é a baixa: tirar do patrimônio é decisão da gerência.</div>
                <Field><label>Motivo (obrigatório quando não consertou)</label><Input value={motivoRetorno} maxLength={400} onChange={(e) => setMotivoRetorno(e.target.value)} /></Field>
                {impRetorno && <div style={{ fontSize: 12, color: BD.grayText, marginTop: 8 }}>{impRetorno}</div>}
              </div>
            )}
            {podeBaixa && (
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Propor baixa</div>
                <div style={{ fontSize: 12, color: BD.grayText, marginBottom: 6 }}>Propor não tira do patrimônio: quem decide é a gerência, na tela de baixas. Uma proposta aberta por peça.</div>
                <Field><label>Motivo</label><Input value={motivoBaixa} maxLength={400} disabled={!!impBaixa} onChange={(e) => setMotivoBaixa(e.target.value)} /></Field>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 }}>
                  {impBaixa && <span style={{ fontSize: 12, color: BD.grayText }}>{impBaixa}</span>}
                </div>
              </div>
            )}
          </div>
        </Bloco>
      )}

      <Bloco titulo={`Razão da peça · ${historicoQ.data.length} lançamento${historicoQ.data.length === 1 ? '' : 's'}`}>
        {historicoQ.isLoading ? <div style={{ textAlign: 'center', padding: 12 }}><Spinner /></div>
          : historicoQ.isError ? (
            <div style={{ fontSize: 13, color: BD.red }}>
              Não foi possível carregar o histórico. <Button $variant="secondary" onClick={() => historicoQ.mutate()}>Tentar de novo</Button>
            </div>
          ) : historicoQ.data.length === 0 ? (
            <div style={{ fontSize: 13, color: BD.grayText }}>Esta peça ainda não tem lançamento no razão.</div>
          ) : (
            <>
              <TabelaWrap>
                <Table>
                  <thead><tr><th>Quando</th><th>Tipo</th><th>Origem → destino</th><th>Ficou</th><th>Referência</th></tr></thead>
                  <tbody>{historicoQ.data.map(linhaHistorico)}</tbody>
                </Table>
              </TabelaWrap>

              <ListaCartoes>
                {historicoQ.data.map((h, k) => {
                  const ficou = ficouDepois(h, historicoQ.data[k + 1])
                  return (
                    <CartaoLinha
                      key={`${h.id}-${k}`}
                      numero={`#${h.id}`}
                      quando={fmtDateTime(h.criadoEm)}
                      titulo={TIPO_MOVIMENTO_LABEL[h.tipo] ?? h.tipo}
                      movimento={(
                        <>
                          {h.origemNome ?? '—'} → {h.destinoNome ?? '—'}
                          {ficou ? <div>ficou {ficou}</div> : null}
                          {ehLancamentoDeEstorno(h) ? <div>estorno do #{h.movimentoEstornoId}</div> : null}
                          {h.estornadoPorId != null ? <div>estornado pelo #{h.estornadoPorId}</div> : null}
                        </>
                      )}
                      referencia={<RefLink tipo={h.referenciaTipo} codigo={h.referenciaId} id={h.referenciaRefId ?? null} documento={h.referenciaDocumento} />}
                      nota={<>{h.usuarioNome}{h.motivo ? ` · ${h.motivo}` : ''}</>}
                    />
                  )
                })}
              </ListaCartoes>
            </>
          )}
      </Bloco>
      </div>

      <Decisao
        estado={leitura.chips[0]}
        desde={leitura.contexto ?? `está em ${ondeEsta(u)}`}
        aviso={u.cicloVida === 'EXTRAVIADO'
          ? 'Peça declarada extraviada: está fora do patrimônio regular até a baixa. Se ela apareceu, registre o reaparecimento nesta ficha.'
          : null}
        principal={principal}
        secundarias={secundarias}
        pendencia={u.baixaPropostaId != null ? 'Já existe uma proposta de baixa aberta para esta peça.' : null}
        busy={busy}
      />
    </DetailLayout>
  )
}

export default EstoqueEquipamento
